/**
 * KnowledgePage.jsx
 *
 * Knowledge Base viewer and update hub for AdLens FX.
 *
 * Sections:
 *   1. KB Summary Cards — all 6 knowledge sources with version, lastUpdated, counts
 *   2. Requirements viewer — drillable list of all requirements per source
 *   3. Recommendations viewer — advisory best practices per source
 *   4. Update Knowledge — AI-powered refresh of any KB entry from official docs
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  KNOWLEDGE_BASE,
  loadKnowledge,
  knowledgeLoaded,
} from '../knowledge';
import {
  knowledgeStatus,
  refreshKnowledgePack,
  listKnowledgeProposals,
  approveKnowledgeProposal,
  dismissKnowledgeProposal,
} from '../api/knowledge';
import { formatFreshnessBadge, changeKindMeta } from '../utils/knowledgeFreshness';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5050';

// Applied AI updates are overrides layered over KNOWLEDGE_BASE. The backend
// persists them server-side (SQLite `config` table, key `knowledge:overrides`)
// via POST /api/knowledge/update, so they're durable and shared across
// browsers/sessions — the backend is the source of truth. localStorage is kept
// only as an instant-paint cache (seeded on load, then reconciled against the
// backend, which wins on conflict). Overrides are not written back into the
// git source files under frontend/src/knowledge/ — export separately for that.
const KB_OVERRIDES_KEY = 'kb_overrides_v1';

function loadKbOverrides() {
  try {
    const raw = localStorage.getItem(KB_OVERRIDES_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

const SEV_STYLE = {
  required:    { background: '#ff4d4d22', color: '#ff7070', border: '1px solid #ff4d4d44' },
  recommended: { background: '#ffaa0022', color: '#ffcc44', border: '1px solid #ffaa0044' },
  info:        { background: '#4488ff22', color: '#66aaff', border: '1px solid #4488ff44' },
};

// ─── Knowledge-freshness UI (WP8, docs/launch-signoff-spec.md §9) ─────────────
// Points this page at the proxy's freshness/refresh/proposal endpoints
// (WP7 — proxy-server/knowledge/freshness.js via api/knowledge.js). This is
// a SEPARATE override mechanism from the legacy `kbOverrides` (localStorage +
// backend SQLite `knowledge:overrides`) system below: the proxy overlay lives
// under proxy-server/data/knowledge-overrides/<id>.json and is what
// GET /proxy/knowledge/status's `overridden` field reflects. Both can be true
// at once for the same pack; they're rendered as distinct chips so the two
// data sources are never conflated.

/** Is this a 409 { error: 'anthropic_api_key_missing' } rejection? Same gate
 * copy used across ChecksPage.jsx / BuildValidationPanel.jsx / LaunchPage.jsx. */
function is409ApiKey(err) {
  return err?.status === 409 && err?.body?.error === 'anthropic_api_key_missing';
}

const FRESHNESS_TONE_STYLE = {
  ok:      { color: '#1e8e5a', background: 'rgba(34,197,94,0.14)' },
  stale:   { color: '#b0761a', background: 'rgba(176,118,26,0.14)' },
  unknown: { color: 'var(--text-muted)', background: 'var(--surface-2)' },
};

function FreshnessBadge({ status }) {
  const { label, tone } = formatFreshnessBadge(status);
  const style = FRESHNESS_TONE_STYLE[tone] || FRESHNESS_TONE_STYLE.unknown;
  return (
    <span
      style={{
        fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
        color: style.color, background: style.background,
      }}
      title="Pack freshness — from GET /proxy/knowledge/status (staleDays vs KNOWLEDGE_STALE_DAYS)"
    >
      {label}
    </span>
  );
}

function OverlayChip() {
  return (
    <span
      style={{
        fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
        color: 'var(--text-muted)', background: 'var(--surface-2)', border: '1px solid var(--border)',
      }}
      title="An approved/dismissed proxy overlay exists for this pack (proxy-server/data/knowledge-overrides/) — distinct from the browser-local 'locally updated' override below"
    >
      ● overlay applied
    </span>
  );
}

const CHANGE_TONE_STYLE = {
  add:    { color: '#1e8e5a', background: 'rgba(34,197,94,0.14)', border: '1px solid rgba(34,197,94,0.35)' },
  update: { color: '#b0761a', background: 'rgba(176,118,26,0.14)', border: '1px solid rgba(176,118,26,0.35)' },
  remove: { color: '#c0392b', background: 'rgba(192,57,43,0.14)', border: '1px solid rgba(192,57,43,0.35)' },
  note:   { color: 'var(--text-muted)', background: 'var(--surface-2)', border: '1px solid var(--border)' },
};

function ChangeKindChip({ kind }) {
  const { label, tone } = changeKindMeta(kind);
  const style = CHANGE_TONE_STYLE[tone] || CHANGE_TONE_STYLE.note;
  return (
    <span style={{
      ...style, fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
      textTransform: 'uppercase', letterSpacing: 0.5,
    }}>
      {label}
    </span>
  );
}

function ChangeBlock({ value }) {
  if (value === undefined) return <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>(none)</span>;
  const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  return (
    <pre style={{
      margin: 0, background: 'var(--surface-2)', border: '1px solid var(--border)',
      borderRadius: 6, padding: 8, fontSize: 11, overflowX: 'auto', maxHeight: 200,
      whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'var(--text-secondary)',
    }}>
      {text}
    </pre>
  );
}

/**
 * ProposalCard — one pending refresh proposal (WP7's
 * POST /proxy/knowledge/refresh output, persisted server-side under
 * data/knowledge-proposals/<packId>.json). `packName` is null when the
 * proposal's packId no longer matches any pack in packStatus() (pack
 * removed/renamed) — approve is disabled in that case but dismiss still
 * works, since dismiss only deletes the proposal + stamps lastVerified on
 * whatever pack IS found (a no-op stamp when the pack is genuinely gone,
 * but never throws — see knowledge/freshness.js's markVerified()).
 */
function ProposalCard({ proposal, packName, onApprove, onDismiss, busy }) {
  const [open, setOpen] = useState(true);
  const changes = Array.isArray(proposal.changes) ? proposal.changes : [];
  const missingPack = !packName;
  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10,
      marginBottom: 14, overflow: 'hidden',
    }}>
      <div style={{
        padding: '12px 16px', background: 'var(--surface-2)', borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
          <div style={{ fontWeight: 700, fontSize: 13 }}>
            {missingPack ? proposal.packId : packName}
            {missingPack && (
              <span style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 700, color: '#c0392b', background: 'rgba(192,57,43,0.14)', padding: '2px 7px', borderRadius: 4 }}>
                pack no longer exists
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
            {proposal.summary || (proposal.noChanges ? 'No changes found.' : `${changes.length} proposed change${changes.length === 1 ? '' : 's'}`)}
            {proposal.ts && <> · {new Date(proposal.ts).toLocaleString()}</>}
          </div>
        </div>
        <button className="btn" style={{ fontSize: 11 }} onClick={() => setOpen(o => !o)}>{open ? '▲' : '▼'}</button>
      </div>

      {open && (
        <div style={{ padding: '4px 16px 14px' }}>
          {changes.length === 0 && (
            <div style={{ padding: '10px 0', color: 'var(--text-muted)', fontSize: 12 }}>
              No changes proposed — approving/dismissing just marks the pack as verified.
            </div>
          )}
          {changes.map((c, i) => (
            <div key={i} style={{ borderBottom: '1px solid var(--border)', padding: '10px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <ChangeKindChip kind={c.kind} />
                {c.requirementId && <code style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.requirementId}</code>}
                {c.sourceUrl && (
                  <a href={c.sourceUrl} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#66aaff', marginLeft: 'auto' }}>
                    source →
                  </a>
                )}
              </div>
              {(c.before !== undefined || c.after !== undefined) && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 3, textTransform: 'uppercase' }}>Before</div>
                    <ChangeBlock value={c.before} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 3, textTransform: 'uppercase' }}>After</div>
                    <ChangeBlock value={c.after} />
                  </div>
                </div>
              )}
            </div>
          ))}
          {Array.isArray(proposal.fetchNotes) && proposal.fetchNotes.length > 0 && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
              {proposal.fetchNotes.map((n, i) => (
                <div key={i}>⚠ {n.url}: {n.error}</div>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button
              className="btn btn-primary"
              style={{ fontSize: 12 }}
              disabled={busy || missingPack}
              title={missingPack ? "Can't approve — this pack no longer exists" : undefined}
              onClick={() => onApprove(proposal.packId)}
            >
              {busy === 'approving' ? 'Approving…' : '✓ Approve'}
            </button>
            <button
              className="btn"
              style={{ fontSize: 12 }}
              disabled={!!busy}
              onClick={() => onDismiss(proposal.packId)}
            >
              {busy === 'dismissing' ? 'Dismissing…' : '✕ Dismiss'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SeverityBadge({ severity }) {
  const style = SEV_STYLE[severity] || SEV_STYLE.info;
  return (
    <span style={{
      ...style,
      fontSize: 10,
      fontWeight: 700,
      padding: '2px 7px',
      borderRadius: 4,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    }}>
      {severity}
    </span>
  );
}

function RequirementRow({ req }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{
      borderBottom: '1px solid var(--border)',
      padding: '10px 0',
    }}>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
        onClick={() => setOpen(o => !o)}
      >
        <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace', minWidth: 180 }}>
          {req.id}
        </span>
        <SeverityBadge severity={req.severity} />
        <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>{req.name}</span>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{open ? '▲' : '▼'}</span>
      </div>

      {open && (
        <div style={{ marginTop: 10, paddingLeft: 12, borderLeft: '2px solid var(--border)' }}>
          <p style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            {req.description}
          </p>
          {req.parameter && (
            <div style={{ fontSize: 12, marginBottom: 4 }}>
              <span style={{ color: 'var(--text-muted)' }}>Parameter: </span>
              <code style={{ background: 'var(--surface-2)', padding: '1px 5px', borderRadius: 3 }}>
                {req.parameter}
              </code>
            </div>
          )}
          {req.expectedPattern && (
            <div style={{ fontSize: 12, marginBottom: 4 }}>
              <span style={{ color: 'var(--text-muted)' }}>Pattern: </span>
              <code style={{ background: 'var(--surface-2)', padding: '1px 5px', borderRadius: 3 }}>
                {req.expectedPattern}
              </code>
            </div>
          )}
          <div style={{ fontSize: 12, marginBottom: 4 }}>
            <span style={{ color: '#ff7070' }}>✗ Fail: </span>
            <span style={{ color: 'var(--text-secondary)' }}>{req.failMessage}</span>
          </div>
          <div style={{ fontSize: 12, marginBottom: 4 }}>
            <span style={{ color: '#44cc88' }}>✓ Pass: </span>
            <span style={{ color: 'var(--text-secondary)' }}>{req.passMessage}</span>
          </div>
          {req.reference && (
            <a
              href={req.reference}
              target="_blank"
              rel="noreferrer"
              style={{ fontSize: 11, color: '#66aaff' }}
            >
              📎 Reference →
            </a>
          )}
        </div>
      )}
    </div>
  );
}

function KBCard({
  kb, summary, overridden, onRevert,
  freshnessStatus, checking, checkResult, hasPendingProposal,
  onCheckUpdates, onJumpToProposal,
}) {
  const [section, setSection] = useState('requirements');
  const reqs = (kb.requirements || []);
  const recs  = (kb.recommendations || []);

  const requiredCount    = reqs.filter(r => r.severity === 'required').length;
  const recommendedCount = reqs.filter(r => r.severity === 'recommended').length;
  const infoCount        = reqs.filter(r => r.severity === 'info').length;

  return (
    <div style={{
      background: 'var(--surface)',
      border: '1px solid var(--border)',
      borderRadius: 10,
      marginBottom: 20,
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '14px 20px',
        background: 'var(--surface-2)',
        borderBottom: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{kb.name}</div>
            <FreshnessBadge status={freshnessStatus} />
            {freshnessStatus?.overridden && <OverlayChip />}
            {overridden && (
              <span
                style={{ fontSize: 10, fontWeight: 700, color: '#1e8e5a', background: 'rgba(34,197,94,0.14)', padding: '2px 7px', borderRadius: 4 }}
                title="Updated via the legacy AI Update run and saved on the backend — applies for everyone; not written into the git source files. Superseded by the proxy freshness/overlay flow above (WP8)."
              >
                ● locally updated (legacy)
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>
            {kb.vendor} · v{kb.version} · updated {kb.lastUpdated}
            {overridden && (
              <>
                {' · '}
                <button
                  onClick={() => onRevert(kb.id)}
                  style={{ background: 'none', border: 'none', padding: 0, color: '#66aaff', fontSize: 11, cursor: 'pointer', textDecoration: 'underline' }}
                >
                  revert
                </button>
              </>
            )}
            {hasPendingProposal && (
              <>
                {' · '}
                <button
                  onClick={onJumpToProposal}
                  style={{ background: 'none', border: 'none', padding: 0, color: '#b0761a', fontSize: 11, cursor: 'pointer', textDecoration: 'underline', fontWeight: 700 }}
                >
                  pending proposal — review ↑
                </button>
              </>
            )}
          </div>
        </div>

        {/* Counts */}
        <div style={{ display: 'flex', gap: 12 }}>
          {requiredCount > 0 && (
            <span style={{ fontSize: 12, color: '#ff7070' }}>
              <b>{requiredCount}</b> required
            </span>
          )}
          {recommendedCount > 0 && (
            <span style={{ fontSize: 12, color: '#ffcc44' }}>
              <b>{recommendedCount}</b> recommended
            </span>
          )}
          {recs.length > 0 && (
            <span style={{ fontSize: 12, color: '#66aaff' }}>
              <b>{recs.length}</b> recommendations
            </span>
          )}
        </div>

        {/* Check for updates — WP8: replaces the legacy backend-AI "Update"
            button (see the deprecated UpdateModal component below). Hits the
            proxy's refresh endpoint instead of the backend's
            /api/knowledge/update. */}
        <button
          className="btn"
          style={{ fontSize: 11, padding: '4px 10px' }}
          disabled={checking}
          onClick={() => onCheckUpdates(kb)}
          title="Fetch this pack's source docs and ask the proxy's AI to propose changes (POST /proxy/knowledge/refresh)"
        >
          {checking ? '⏳ Checking…' : '🔄 Check for updates'}
        </button>
      </div>

      {/* Refresh result — transient inline feedback from the last "Check for
          updates" click on this pack (issues / 409 key hint / no-changes ack).
          Cleared automatically once the user checks again. */}
      {checkResult && (
        <div style={{
          padding: '8px 20px',
          borderBottom: '1px solid var(--border)',
          fontSize: 12,
          background: checkResult.type === 'error' || checkResult.type === 'issues' ? '#ff4d4d14'
            : checkResult.type === 'noApiKey' ? '#ffaa0014'
              : '#44cc8814',
          color: checkResult.type === 'error' || checkResult.type === 'issues' ? '#ff7070'
            : checkResult.type === 'noApiKey' ? '#ffcc44'
              : '#44cc88',
        }}>
          {checkResult.type === 'noChanges' && '✓ No changes found — marked current.'}
          {checkResult.type === 'proposalReady' && '✓ Proposed changes are ready for review above.'}
          {checkResult.type === 'noApiKey' && (
            <>Set <code>ANTHROPIC_API_KEY</code> on the proxy to check for pack updates.</>
          )}
          {checkResult.type === 'issues' && (
            <div>
              Couldn&apos;t refresh this pack:
              <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                {(checkResult.issues || []).map((iss, i) => <li key={i}>{String(iss)}</li>)}
              </ul>
            </div>
          )}
          {checkResult.type === 'error' && (checkResult.message || 'Refresh failed.')}
        </div>
      )}

      {/* Source URLs */}
      <div style={{ padding: '8px 20px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {(kb.sourceUrls || []).map(url => (
          <a key={url} href={url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#66aaff' }}>
            {new URL(url).hostname}
          </a>
        ))}
      </div>

      {/* Section tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)' }}>
        {['requirements', 'recommendations'].map(s => (
          <button
            key={s}
            onClick={() => setSection(s)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '8px 16px', fontSize: 12, fontWeight: 600,
              color: section === s ? '#66aaff' : 'var(--text-muted)',
              borderBottom: section === s ? '2px solid #66aaff' : '2px solid transparent',
            }}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
            {' '}
            <span style={{ opacity: 0.6 }}>
              ({s === 'requirements' ? reqs.length : recs.length})
            </span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding: '4px 20px 16px' }}>
        {section === 'requirements' && reqs.map(r => (
          <RequirementRow key={r.id} req={r} />
        ))}
        {section === 'recommendations' && (
          recs.length === 0
            ? <div style={{ padding: '16px 0', color: 'var(--text-muted)', fontSize: 13 }}>No recommendations defined.</div>
            : recs.map(r => (
              <div key={r.id} style={{ borderBottom: '1px solid var(--border)', padding: '10px 0' }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{r.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 4 }}>
                  {r.description}
                </div>
                {r.reference && (
                  <a href={r.reference} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#66aaff' }}>
                    📎 Reference →
                  </a>
                )}
              </div>
            ))
        )}
      </div>
    </div>
  );
}

// ─── Update Modal (DEPRECATED — WP8, docs/launch-signoff-spec.md §9) ─────────
//
// Superseded by the proxy-based "Check for updates" flow in KBCard above
// (POST /proxy/knowledge/refresh -> ProposalCard review -> approve/dismiss).
// This component called the BACKEND's `POST /api/knowledge/update`
// (backend/index.js), which writes into backend SQLite (`config` table,
// key `knowledge:overrides`) — a path `GET /proxy/knowledge` never saw, which
// was exactly the asymmetry WP7/WP8 fixed (knowledge is now proxy-owned
// end-to-end). Per spec: leave the backend route + this client function in
// place (do not delete — rollback path), but it is no longer wired to any
// button; nothing in this page renders <UpdateModal> anymore.
function UpdateModal({ kb, onClose, onApply }) {
  const [status, setStatus]   = useState('idle'); // idle | loading | done | error
  const [result, setResult]   = useState(null);
  const [errMsg, setErrMsg]   = useState('');
  const [copied, setCopied]   = useState(false);

  const runUpdate = useCallback(async () => {
    setStatus('loading');
    setResult(null);
    setErrMsg('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/knowledge/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kbId: kb.id, currentJson: kb }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || err.message || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setResult(data);
      setStatus('done');
      // Auto-apply: a parsed JSON response immediately refreshes the card in this
      // browser. It's still just a local override (see KB_OVERRIDES_KEY above) —
      // someone needs to copy it into the source file for it to be canonical.
      if (data.updatedJson) {
        onApply(kb.id, data.updatedJson);
      }
    } catch (e) {
      setErrMsg(e.message);
      setStatus('error');
    }
  }, [kb, onApply]);

  const copyJson = useCallback(() => {
    const txt = result?.updatedJson
      ? JSON.stringify(result.updatedJson, null, 2)
      : result?.suggestions || '';
    navigator.clipboard.writeText(txt).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [result]);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: '#000a', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 12,
        width: 720,
        maxHeight: '85vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* Modal header */}
        <div style={{
          padding: '16px 20px',
          background: 'var(--surface-2)',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>Update Knowledge — {kb.name}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
              AI will review official docs and suggest updated requirements
            </div>
          </div>
          <button className="btn" onClick={onClose} style={{ fontSize: 12 }}>✕ Close</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
          {status === 'idle' && (
            <div>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16, lineHeight: 1.5 }}>
                This will send the current <b>{kb.name}</b> requirements to the AI along with its
                official source documentation URLs. The AI will review them for accuracy and suggest
                updated requirements, severity levels, and new best practices.
              </p>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 20 }}>
                Sources: {(kb.sourceUrls || []).join(', ')}
              </p>
              <button
                className="btn btn-primary"
                style={{ fontSize: 13, padding: '8px 20px' }}
                onClick={runUpdate}
              >
                🔄 Run AI Update
              </button>
            </div>
          )}

          {status === 'loading' && (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <div style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 12 }}>
                Analysing docs with AI…
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                This may take 15–30 seconds
              </div>
            </div>
          )}

          {status === 'error' && (
            <div style={{ padding: 16, background: '#ff4d4d22', border: '1px solid #ff4d4d55', borderRadius: 8 }}>
              <div style={{ fontWeight: 600, color: '#ff7070', marginBottom: 6 }}>Update failed</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{errMsg}</div>
              <button
                className="btn"
                style={{ marginTop: 12, fontSize: 12 }}
                onClick={() => setStatus('idle')}
              >
                Try again
              </button>
            </div>
          )}

          {status === 'done' && result && (
            <div>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16,
                padding: '10px 14px',
                background: '#44cc8822',
                border: '1px solid #44cc8844',
                borderRadius: 8,
              }}>
                <span style={{ color: '#44cc88', fontWeight: 600 }}>
                  {result.updatedJson ? '✓ Card updated' : '✓ Update complete'}
                </span>
                {result.updatedJson && (
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    · v{result.updatedJson.version} · {result.updatedJson.requirements?.length} requirements
                  </span>
                )}
                <div style={{ flex: 1 }} />
                <button
                  className="btn"
                  style={{ fontSize: 11, padding: '4px 10px' }}
                  onClick={copyJson}
                >
                  {copied ? '✓ copied' : '📋 Copy JSON'}
                </button>
              </div>

              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
                {result.updatedJson
                  ? 'Saved to the backend — this update now applies across browsers and survives refresh/redeploy. (It is not written back into the git source files; export separately if you want it in version control.)'
                  : 'AI returned suggestions as text (could not parse as JSON) — the card was not changed. Review and apply manually.'}
              </div>

              <pre style={{
                background: 'var(--surface-2)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                padding: 14,
                fontSize: 11,
                overflowX: 'auto',
                maxHeight: 400,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                color: 'var(--text-secondary)',
              }}>
                {result.updatedJson
                  ? JSON.stringify(result.updatedJson, null, 2)
                  : result.suggestions}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function KnowledgePage() {
  const [search,      setSearch]      = useState('');
  const [filterKb,    setFilterKb]    = useState('all');
  const [filterSev,   setFilterSev]   = useState('all');

  // ── Knowledge freshness (WP8, docs/launch-signoff-spec.md §9) ────────────
  // `freshnessStatus` — map of packId -> packStatus() entry from
  // GET /proxy/knowledge/status. `freshnessError` distinguishes "proxy
  // unreachable" from "0 packs" so the page can show a banner without hiding
  // the rest of the (still-usable) page — see loadFreshnessAndProposals().
  const [freshnessStatus, setFreshnessStatus] = useState({});
  const [freshnessError, setFreshnessError] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [checking, setChecking] = useState({});      // packId -> true while a refresh is in flight
  const [checkResults, setCheckResults] = useState({}); // packId -> { type, issues?, message? } (last refresh outcome)
  const [proposalBusy, setProposalBusy] = useState({});  // packId -> 'approving' | 'dismissing'
  const proposalsRef = React.useRef(null);

  const loadFreshnessAndProposals = useCallback(async () => {
    const [statusResult, proposalsResult] = await Promise.allSettled([
      knowledgeStatus(),
      listKnowledgeProposals(),
    ]);
    if (statusResult.status === 'fulfilled') {
      const byId = {};
      for (const s of statusResult.value) { if (s && s.id) byId[s.id] = s; }
      setFreshnessStatus(byId);
      setFreshnessError(null);
    } else {
      setFreshnessError(statusResult.reason);
    }
    if (proposalsResult.status === 'fulfilled') {
      setProposals(proposalsResult.value);
      // A successful proposals fetch means the proxy IS reachable even if
      // status somehow failed transiently — but don't clobber a genuine
      // status error; only clear when status also succeeded (handled above).
    } else if (statusResult.status !== 'fulfilled') {
      // Both failed — keep the first (status) error already set.
    }
  }, []);

  useEffect(() => { loadFreshnessAndProposals(); }, [loadFreshnessAndProposals]);

  const handleCheckForUpdates = useCallback(async (kb) => {
    setChecking(prev => ({ ...prev, [kb.id]: true }));
    setCheckResults(prev => { const next = { ...prev }; delete next[kb.id]; return next; });
    try {
      const result = await refreshKnowledgePack(kb.id);
      if (result && result.ok && result.proposal) {
        if (result.proposal.noChanges) {
          // Spec: noChanges is a first-class good outcome — stamp
          // lastVerified via dismiss (no changes to review) and reload.
          try { await dismissKnowledgeProposal(kb.id); } catch { /* best-effort stamp */ }
          setCheckResults(prev => ({ ...prev, [kb.id]: { type: 'noChanges' } }));
        } else {
          setCheckResults(prev => ({ ...prev, [kb.id]: { type: 'proposalReady' } }));
        }
      } else {
        setCheckResults(prev => ({ ...prev, [kb.id]: { type: 'issues', issues: ['Unexpected response from the proxy.'] } }));
      }
    } catch (err) {
      if (is409ApiKey(err)) {
        setCheckResults(prev => ({ ...prev, [kb.id]: { type: 'noApiKey' } }));
      } else if (err?.status === 400 && Array.isArray(err?.body?.issues)) {
        setCheckResults(prev => ({ ...prev, [kb.id]: { type: 'issues', issues: err.body.issues } }));
      } else {
        setCheckResults(prev => ({ ...prev, [kb.id]: { type: 'error', message: err?.message || 'Refresh failed.' } }));
      }
    } finally {
      setChecking(prev => { const next = { ...prev }; delete next[kb.id]; return next; });
      loadFreshnessAndProposals();
    }
  }, [loadFreshnessAndProposals]);

  const handleApproveProposal = useCallback(async (packId) => {
    setProposalBusy(prev => ({ ...prev, [packId]: 'approving' }));
    try {
      await approveKnowledgeProposal(packId);
    } catch (err) {
      setCheckResults(prev => ({
        ...prev,
        [packId]: { type: 'issues', issues: Array.isArray(err?.body?.issues) ? err.body.issues : [err?.message || 'Approve failed.'] },
      }));
    } finally {
      setProposalBusy(prev => { const next = { ...prev }; delete next[packId]; return next; });
      loadFreshnessAndProposals();
    }
  }, [loadFreshnessAndProposals]);

  const handleDismissProposal = useCallback(async (packId) => {
    setProposalBusy(prev => ({ ...prev, [packId]: 'dismissing' }));
    try {
      await dismissKnowledgeProposal(packId);
    } catch (err) {
      setCheckResults(prev => ({
        ...prev,
        [packId]: { type: 'issues', issues: Array.isArray(err?.body?.issues) ? err.body.issues : [err?.message || 'Dismiss failed.'] },
      }));
    } finally {
      setProposalBusy(prev => { const next = { ...prev }; delete next[packId]; return next; });
      loadFreshnessAndProposals();
    }
  }, [loadFreshnessAndProposals]);

  const jumpToProposals = useCallback(() => {
    proposalsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // Local overrides applied by a successful AI Update (see KB_OVERRIDES_KEY above).
  const [kbOverrides, setKbOverrides] = useState(loadKbOverrides);

  // KNOWLEDGE_BASE is fetched from the proxy at runtime (src/knowledge/index.js)
  // — App.js kicks off loadKnowledge() at boot, but this page can be the first
  // thing a user hits, so re-trigger it here too (idempotent) and track when
  // the packs actually land so the `resolvedKB` memo below recomputes.
  const [kbLoaded, setKbLoaded] = useState(knowledgeLoaded());
  useEffect(() => {
    let alive = true;
    loadKnowledge().then(() => { if (alive) setKbLoaded(knowledgeLoaded()); });
    return () => { alive = false; };
  }, []);

  // Only caller was the now-unrendered <UpdateModal> (see its deprecation
  // comment above) — kept, not deleted, so a rollback to the legacy Update
  // flow doesn't need to reconstruct this.
  const applyKbOverride = useCallback((kbId, updatedJson) => {
    setKbOverrides(prev => {
      const next = { ...prev, [kbId]: updatedJson };
      try { localStorage.setItem(KB_OVERRIDES_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const revertKbOverride = useCallback((kbId) => {
    setKbOverrides(prev => {
      if (!(kbId in prev)) return prev;
      const next = { ...prev };
      delete next[kbId];
      try { localStorage.setItem(KB_OVERRIDES_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
    // Fire-and-forget: clear the server copy too so revert isn't just local.
    fetch(`${BACKEND_URL}/api/knowledge/overrides/${encodeURIComponent(kbId)}`, { method: 'DELETE' }).catch(() => {});
  }, []);

  // The backend (SQLite `config` table) is the source of truth for overrides —
  // localStorage above is only an instant-paint seed. Reconcile on mount, with
  // the backend winning any conflict, then re-cache the merged result locally.
  useEffect(() => {
    let alive = true;
    fetch(`${BACKEND_URL}/api/knowledge/overrides`)
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(serverOverrides => {
        if (!alive) return;
        if (!serverOverrides || typeof serverOverrides !== 'object') return;
        setKbOverrides(prev => {
          const merged = { ...prev, ...serverOverrides };
          try { localStorage.setItem(KB_OVERRIDES_KEY, JSON.stringify(merged)); } catch {}
          return merged;
        });
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const resolvedKB = useMemo(
    () => KNOWLEDGE_BASE.map(kb => (kbOverrides[kb.id] ? { ...kb, ...kbOverrides[kb.id] } : kb)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [kbOverrides, kbLoaded]
  );

  // Cheap self-diagnosing AI readiness probe (no Anthropic call). Distinguishes
  // "ready" from "key missing on backend" from "backend unreachable" (the CORS/
  // ERR_FAILED case), so Update failures aren't opaque.
  const [aiStatus, setAiStatus] = useState('checking'); // checking | ready | no-key | unreachable
  const [aiMeta, setAiMeta] = useState(null);            // { model, webSearch }
  useEffect(() => {
    let alive = true;
    fetch(`${BACKEND_URL}/api/knowledge/ai-status`)
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(d => { if (!alive) return; setAiStatus(d?.ready ? 'ready' : 'no-key'); setAiMeta({ model: d?.model, webSearch: d?.webSearch }); })
      .catch(() => { if (alive) setAiStatus('unreachable'); });
    return () => { alive = false; };
  }, []);

  const summary = useMemo(() => resolvedKB.map(kb => ({
    id: kb.id,
    name: kb.name,
    vendor: kb.vendor,
    version: kb.version,
    lastUpdated: kb.lastUpdated,
    sourceUrls: kb.sourceUrls,
    requirementCount: (kb.requirements || []).length,
    recommendationCount: (kb.recommendations || []).length,
  })), [resolvedKB]);

  // Search + filter across all requirements
  const allRequirements = useMemo(() => {
    const out = [];
    for (const kb of resolvedKB) {
      for (const req of (kb.requirements || [])) {
        out.push({ ...req, _kbName: kb.name, _kbId: kb.id });
      }
    }
    return out;
  }, [resolvedKB]);

  const filteredReqs = useMemo(() => {
    if (!search && filterKb === 'all' && filterSev === 'all') return null; // show by-card view
    return allRequirements.filter(r => {
      const matchSearch = !search ||
        r.name.toLowerCase().includes(search.toLowerCase()) ||
        r.id.toLowerCase().includes(search.toLowerCase()) ||
        (r.description || '').toLowerCase().includes(search.toLowerCase()) ||
        (r.parameter || '').toLowerCase().includes(search.toLowerCase());
      const matchKb  = filterKb === 'all'  || r._kbId === filterKb;
      const matchSev = filterSev === 'all' || r.severity === filterSev;
      return matchSearch && matchKb && matchSev;
    });
  }, [search, filterKb, filterSev, allRequirements]);

  const isFiltered = filteredReqs !== null;

  // Summary stats
  const stats = useMemo(() => ({
    total: allRequirements.length,
    required:    allRequirements.filter(r => r.severity === 'required').length,
    recommended: allRequirements.filter(r => r.severity === 'recommended').length,
    recs: resolvedKB.reduce((a, kb) => a + (kb.recommendations || []).length, 0),
  }), [allRequirements, resolvedKB]);

  // Lightweight loading state — packs are fetched from the proxy at runtime
  // (no more bundled JSON), so on a cold app boot this page can render before
  // GET /proxy/knowledge resolves.
  if (!kbLoaded && KNOWLEDGE_BASE.length === 0) {
    return (
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 20px', color: 'var(--text-muted)', fontSize: 13 }}>
        Loading knowledge base…
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 20px' }}>

      {/* Page header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Knowledge Base</h1>
        <p style={{ margin: '6px 0 0', color: 'var(--text-muted)', fontSize: 13 }}>
          Offline expert knowledge for {resolvedKB.length} AdTech SDKs and specs ·{' '}
          <span style={{ color: '#ff7070' }}>{stats.required} required</span> ·{' '}
          <span style={{ color: '#ffcc44' }}>{stats.recommended} recommended</span> ·{' '}
          <span style={{ color: '#66aaff' }}>{stats.recs} advisory</span>
        </p>
        {(() => {
          const short = (m) => String(m || '').replace('claude-', '').replace(/-\d{6,}$/, '');
          const cfg = {
            checking:    { c: 'var(--text-muted)', bg: 'var(--bg-raised, #eef2f6)', label: 'Checking AI (legacy backend path)…' },
            ready:       { c: '#1e8e5a', bg: 'rgba(34,197,94,0.12)', label: `AI ready (legacy backend path)${aiMeta?.model ? ` · ${short(aiMeta.model)}` : ''}${aiMeta?.webSearch ? ' · web search' : ''}` },
            'no-key':    { c: '#b0761a', bg: 'rgba(176,118,26,0.14)', label: 'AI key not set on backend (legacy path — unused by "Check for updates")' },
            unreachable: { c: '#c0392b', bg: 'rgba(192,57,43,0.14)', label: 'Backend unreachable (api.adlensfx.com) — legacy path only' },
          }[aiStatus];
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10, padding: '3px 10px', borderRadius: 12, fontSize: 11.5, fontWeight: 600, color: cfg.c, background: cfg.bg }} title="Legacy backend AI readiness probe (superseded by the proxy 'Check for updates' flow below, which has its own per-pack error handling)">
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: cfg.c, flexShrink: 0 }} />
              {cfg.label}
            </span>
          );
        })()}
      </div>

      {/* Proxy-unreachable banner (WP8) — the freshness/refresh/proposal
          endpoints live on the proxy, a separate service from the backend
          above. The rest of the page (KB browsing) still renders normally —
          this only affects the freshness badges/Check-for-updates flow. */}
      {freshnessError && (
        <div style={{
          marginBottom: 20, padding: '10px 16px', borderRadius: 8,
          background: 'rgba(192,57,43,0.12)', border: '1px solid rgba(192,57,43,0.35)',
          color: '#c0392b', fontSize: 12.5,
        }}>
          Couldn&apos;t reach the proxy for knowledge freshness ({freshnessError?.message || 'unknown error'}).
          Pack browsing below still works; freshness badges and &quot;Check for updates&quot; are unavailable until the proxy is reachable.
        </div>
      )}

      {/* Pending Knowledge Updates (WP8) — proposals from a previous "Check
          for updates" run that haven't been approved/dismissed yet. Rendered
          for ALL pending proposals (not just ones matching a currently-known
          pack), so a proposal for a since-removed pack still surfaces with a
          warning instead of vanishing silently. */}
      {proposals.length > 0 && (
        <div ref={proposalsRef} style={{ marginBottom: 24 }}>
          <h2 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 700 }}>
            Pending Knowledge Updates <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>({proposals.length})</span>
          </h2>
          {proposals.map(p => {
            const pack = resolvedKB.find(kb => kb.id === p.packId);
            return (
              <ProposalCard
                key={p.packId}
                proposal={p}
                packName={pack ? pack.name : null}
                busy={proposalBusy[p.packId]}
                onApprove={handleApproveProposal}
                onDismiss={handleDismissProposal}
              />
            );
          })}
        </div>
      )}

      {/* Summary stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
        {summary.map(s => (
          <div key={s.id} style={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            padding: '12px 16px',
            cursor: 'pointer',
          }}
            onClick={() => setFilterKb(filterKb === s.id ? 'all' : s.id)}
          >
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{s.name}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
              v{s.version} · {s.lastUpdated}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <span style={{ fontSize: 11, color: '#ff7070' }}>{s.requirementCount} reqs</span>
              <span style={{ fontSize: 11, color: '#66aaff' }}>{s.recommendationCount} recs</span>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex', gap: 10, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap',
      }}>
        <input
          type="text"
          placeholder="Search requirements…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            flex: 1, minWidth: 200,
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            padding: '7px 12px',
            fontSize: 13,
            color: 'var(--text)',
          }}
        />
        <select
          value={filterKb}
          onChange={e => setFilterKb(e.target.value)}
          style={{
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 6, padding: '7px 10px', fontSize: 12, color: 'var(--text)',
          }}
        >
          <option value="all">All sources</option>
          {summary.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select
          value={filterSev}
          onChange={e => setFilterSev(e.target.value)}
          style={{
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 6, padding: '7px 10px', fontSize: 12, color: 'var(--text)',
          }}
        >
          <option value="all">All severity</option>
          <option value="required">Required</option>
          <option value="recommended">Recommended</option>
          <option value="info">Info</option>
        </select>
        {(search || filterKb !== 'all' || filterSev !== 'all') && (
          <button
            className="btn"
            style={{ fontSize: 11 }}
            onClick={() => { setSearch(''); setFilterKb('all'); setFilterSev('all'); }}
          >
            ✕ Clear
          </button>
        )}
      </div>

      {/* Filtered search results */}
      {isFiltered ? (
        <div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
            {filteredReqs.length} result{filteredReqs.length !== 1 ? 's' : ''}
          </div>
          {filteredReqs.length === 0 && (
            <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '24px 0' }}>
              No requirements match your filters.
            </div>
          )}
          {filteredReqs.map(r => (
            <div key={r.id} style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: '10px 16px',
              marginBottom: 8,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                <code style={{ fontSize: 11, color: 'var(--text-muted)' }}>{r.id}</code>
                <SeverityBadge severity={r.severity} />
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r._kbName}</span>
              </div>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{r.name}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                {r.description}
              </div>
              {r.parameter && (
                <div style={{ fontSize: 11, marginTop: 6 }}>
                  <span style={{ color: 'var(--text-muted)' }}>param: </span>
                  <code style={{ background: 'var(--surface-2)', padding: '1px 5px', borderRadius: 3 }}>
                    {r.parameter}
                  </code>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        // Default card view — one card per KB source
        resolvedKB.map(kb => (
          <KBCard
            key={kb.id}
            kb={kb}
            summary={summary.find(s => s.id === kb.id)}
            overridden={!!kbOverrides[kb.id]}
            onRevert={revertKbOverride}
            freshnessStatus={freshnessStatus[kb.id]}
            checking={!!checking[kb.id]}
            checkResult={checkResults[kb.id]}
            hasPendingProposal={proposals.some(p => p.packId === kb.id)}
            onCheckUpdates={handleCheckForUpdates}
            onJumpToProposal={jumpToProposals}
          />
        ))
      )}

      {/* No <UpdateModal> render here anymore (WP8) — see the deprecation
          comment on the component definition above. `updateTarget` state /
          `applyKbOverride`'s only caller were removed with it; the legacy
          local-override mechanism (kbOverrides/revert) is otherwise
          untouched and still works for overrides applied before this
          change. */}
    </div>
  );
}
